import { SearchOutlined } from "@ant-design/icons"
import { Card, Flex, Skeleton } from "antd"
import React, { useCallback, useEffect, useMemo } from "react"

import { SearchBar } from "../../../../../../components/Input/SearchBar.tsx"
import { useAppSelector } from "../../../../../../hooks/use-app-selector.ts"
import type { AlbumFilters } from "../../../../../Album/redux/album.types.ts"
import { type AlbumFilterOptions } from "../../../../../Album/types/album.types.ts"
import { ResolvedSelect } from "../../../../../../components/Select/ResolvedSelect.tsx"

const FILTER_CONFIGS: Array<{
  key: keyof AlbumFilters
  optionsKey: keyof AlbumFilterOptions
  placeholder: string
  width: number
}> = [
  {
    key: "file_type",
    optionsKey: "file_types",
    placeholder: "File type",
    width: 120
  },
  {
    key: "milestone",
    optionsKey: "milestones",
    placeholder: "Select milestone",
    width: 150
  },
  {
    key: "checklist_item",
    optionsKey: "checklist_items",
    placeholder: "Select checklist",
    width: 150
  },
  {
    key: "invoice",
    optionsKey: "invoices",
    placeholder: "Select invoice",
    width: 150
  },
  {
    key: "financial_item",
    optionsKey: "financial_items",
    placeholder: "Select finance",
    width: 150
  }
]

interface ProjectViewAlbumSectionFiltersProps {
  filters: AlbumFilters
  onFilterChange: (data: Partial<AlbumFilters>) => void
  getFilterOptions: () => Promise<AlbumFilterOptions | null>
}

export function ProjectViewAlbumSectionFilters({
  filters,
  onFilterChange,
  getFilterOptions
}: ProjectViewAlbumSectionFiltersProps) {
  const fetching = useAppSelector(
    (state) => state.album.albumFetchingFilterOptions
  )
  const options = useAppSelector((state) => state.album.albumFilterOptions)

  const handleChange = useCallback(
    (key: keyof AlbumFilters) => (value: string | undefined | null) =>
      onFilterChange({ [key]: value || null }),
    [onFilterChange]
  )

  const activeFilters = useMemo(
    () =>
      FILTER_CONFIGS.filter(
        ({ optionsKey }) => !!options?.[optionsKey]?.length
      ),
    [options]
  )

  useEffect(() => {
    void getFilterOptions()
  }, [getFilterOptions])

  if (fetching) {
    return <Skeleton.Node className="w-full h-[50px]" active />
  }

  return (
    <Card classNames={{ body: "p-2" }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
        <Flex align="center" gap={12} wrap="wrap">
          {activeFilters.map(({ key, optionsKey, placeholder, width }) => (
            <ResolvedSelect
              key={key}
              allowClear
              value={filters?.[key] as string}
              style={{ width }}
              placeholder={placeholder}
              onChange={handleChange(key)}
              options={options?.[optionsKey]}
            />
          ))}
        </Flex>

        <SearchBar
          placeholder="Search files..."
          prefix={<SearchOutlined className="text-gray-400" />}
          className="w-48 md:w-64 flex-none"
          defaultValue={filters?.q || ""}
          onSearch={handleChange("q")}
        />
      </Flex>
    </Card>
  )
}
